import { Skeleton } from '@ordo/ui/skeleton'
import { type ComponentType, lazy, type ReactElement, type ReactNode, Suspense } from 'react'

/**
 * Generic route-level chunk-load fallback. Shown only while a lazy page's JS
 * chunk downloads (typically well under a data fetch), so a minimal header +
 * content block is enough — pages with a purpose-built skeleton pass it as
 * the `fallback` arg instead. Never a "Loading…" text per the app convention.
 */
export function PageSkeletonFallback(): ReactElement {
  return (
    <div className="flex flex-col gap-4 p-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

/**
 * Route-level code splitting helper: `element: lazyElement(() =>
 * import('@/pages/foo'), 'FooPage')`. Wraps React.lazy over a NAMED export
 * (our pages don't use default exports) in a Suspense boundary, so each page
 * becomes its own Vite chunk and the boot bundle only carries the routes a
 * user actually visits. Keep the common parent entry pages (HomePage switch,
 * signin, day views) statically imported — see routes/index.tsx.
 */
export function lazyElement<M extends Record<string, unknown>>(
  load: () => Promise<M>,
  name: keyof M & string,
  fallback?: ReactNode,
): ReactElement {
  const Page = lazy(async () => ({ default: (await load())[name] as ComponentType }))
  return <Suspense fallback={fallback ?? <PageSkeletonFallback />}>{<Page />}</Suspense>
}
